import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { app } from "../firebaseconfig";
import {
  Row,
  Col,
  Button,
  Drawer,
  Divider,
  Modal,
  Input,
  Select,
  Space,
  Typography,
  message,
  Avatar,
  Dropdown,
  Menu,
  Grid,
} from "antd";
import {
  MenuOutlined,
  LogoutOutlined,
  UserOutlined,
  GoogleOutlined,
  PhoneOutlined,
  FileSearchOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import {
  getAuth,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import AuthTabsModal from "./AuthTabsModal";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMyDetails,
  getNavbarDetails,
  loginWithGoogle,
  updatePhoneNumber,
  getWhatsAppCaptcha,
  sendWhatsAppOtp,
  verifyWhatsAppOtp,
} from "../services/api";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
const { useBreakpoint } = Grid;

const Navbar = () => {
  const location = useLocation();
  const auth = getAuth(app);
  const provider = new GoogleAuthProvider();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [countryCode, setCountryCode] = useState("+91");
  const [waNumber, setWaNumber] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [captchaCode, setCaptchaCode] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [otpError, setOtpError] = useState("");
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [verifyAttempts, setVerifyAttempts] = useState(0);
  const queryClient = useQueryClient();
  const accessToken = Cookies.get("token");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authQuery = params.get("auth");
    if (authQuery === "signin") {
      setAuthModalOpen(true);
    }
  }, [location.search]);

  useEffect(() => {
    if (!otpSent || secondsRemaining <= 0) return;

    const timer = setInterval(() => {
      setSecondsRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [otpSent, secondsRemaining > 0]);

  const { data: userDetails } = useQuery({
    queryKey: ["myDetails"],
    queryFn: async () => {
      const response = await getMyDetails(accessToken);
      return response.data;
    },
    enabled: !!accessToken,
    refetchOnWindowFocus: false,
  });

  const { data: navbarDetails } = useQuery({
    queryKey: ["navbar"],
    queryFn: async () => {
      const response = await getNavbarDetails();
      return response;
    },
    refetchOnWindowFocus: false,
  });

  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);

  const openAuthModal = () => {
    closeDrawer();
    setAuthModalOpen(true);
  };
  const closeAuthModal = () => setAuthModalOpen(false);

  const loginMutation = useMutation({
    mutationFn: loginWithGoogle,
    onSuccess: (data) => {
      const userData = data.data;
      const backendToken = data.token;

      if (userData.phoneNo) {
        completeLogin(userData, backendToken);
        return;
      }

      setPendingUser({ ...userData, token: backendToken });
      setCountryCode("+91");
      setWaNumber("");
      setCaptchaCode("");
      setOtp("");
      setOtpSent(false);
      setCaptcha(null);
      setAuthModalOpen(false);
      setPhoneModalOpen(true);
      setLoading(false);
    },
    onError: (error) => {
      console.error("Login failed", error);
      message.error(
        "Login failed: " + (error.response?.data?.message || error.message),
      );
      setLoading(false);
    },
  });

  const completeLogin = (userData, token) => {
    if (token) {
      Cookies.set("token", token, {
        expires: 7,
        secure: true,
        sameSite: "Strict",
      });
    }
    message.success("Successfully signed in!");
    setAuthModalOpen(false);
    setLoading(false);
    queryClient.invalidateQueries(["myDetails"]);

    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");

    if (redirect) {
      navigate(redirect, { replace: true });
    } else {
      if (params.get("auth")) {
        params.delete("auth");
        navigate(
          {
            pathname: location.pathname,
            search: params.toString(),
          },
          { replace: true },
        );
      }
    }
  };

  const refreshWhatsAppCaptcha = async () => {
    try {
      const response = await getWhatsAppCaptcha();
      setCaptcha(response.data || response);
      setCaptchaCode("");
    } catch (error) {
      console.error("CAPTCHA load failed", error);
      message.error("Unable to load CAPTCHA. Please try again.");
    }
  };

  useEffect(() => {
    if (phoneModalOpen && pendingUser) refreshWhatsAppCaptcha();
    // Load a fresh challenge each time the verification modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneModalOpen, pendingUser]);

  const fullWhatsAppNumber = waNumber;

  const handleSendWhatsAppOtp = async (isResend = false) => {
    if (!pendingUser) return;
    const digits = waNumber.replace(/\D/g, "");
    if (digits.length < 6 || digits.length > 14) {
      message.error("Please enter a valid WhatsApp number.");
      return;
    }
    if (!captcha?.captchaId || !captchaCode.trim()) {
      message.error("Please enter the CAPTCHA shown below.");
      return;
    }

    setLoading(true);
    try {
      await sendWhatsAppOtp({
        mobile: fullWhatsAppNumber,
        captchaId: captcha.captchaId,
        captchaCode: captchaCode.trim(),
        name: pendingUser.name || pendingUser.displayName || "",
        email: pendingUser.email || "",
        isResend,
        appKey: process.env.REACT_APP_WA_AUTH_APP_KEY || "dive_hrzn",
      });
      setOtpSent(true);
      setOtp("");
      setOtpError("");
      setVerifyAttempts(0);
      setSecondsRemaining(300);

      message.success("OTP sent to your WhatsApp.");
    } catch (error) {
      const text =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Could not send OTP.";
      message.error(text);
      // CAPTCHA challenges are single-use, including failed submissions.
      await refreshWhatsAppCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyWhatsAppOtp = async () => {
    if (!pendingUser) return;
    if (!/^\d{6}$/.test(otp.trim())) {
      message.error("Enter the 6-digit OTP sent to your WhatsApp.");
      return;
    }
    setOtpError("");
    setLoading(true);
    try {
      await verifyWhatsAppOtp({
        mobile: fullWhatsAppNumber,
        otp: otp.trim(),
        appKey: process.env.REACT_APP_WA_AUTH_APP_KEY || "dive_hrzn",
        email: pendingUser.email || "",
      });
      // Save the number to the DIVE HRZN profile only after OTP verification succeeds.
      await updatePhoneNumber({
        phoneNo: fullWhatsAppNumber,
        accessToken: pendingUser.token,
      });
      const updatedUser = { ...pendingUser, phoneNo: fullWhatsAppNumber };
      setPhoneModalOpen(false);
      setPendingUser(null);
      completeLogin(updatedUser, updatedUser.token);
      queryClient.invalidateQueries({ queryKey: ["myDetails"] });
    } catch (error) {
      console.error("WhatsApp verification failed", error);

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "OTP verification failed. Please try again.";

      if (error.response?.status === 400 || error.response?.status === 429) {
        setVerifyAttempts((prev) => Math.min(prev + 1, 5));
      }

      setOtpError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await user.getIdToken();
      loginMutation.mutate(idToken);
    } catch (err) {
      console.error(err);
      message.error(err.message || "Google sign-in failed");
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.warn(e);
    }
    Cookies.remove("token");
    message.success("Logged out");
    navigate("/");
    window.location.reload();
  };

  const goto = (path) => {
    navigate(path);
    closeDrawer();
  };

  const profileMenuItems = [
    {
      key: "profile",
      label: "Profile",
      icon: <UserOutlined />,
      onClick: () => navigate("/profile"),
    },
    {
      key: "myEnquiries",
      label: "My Enquiries",
      icon: <FileSearchOutlined />,
      onClick: () => navigate("/my-enquiries"),
    },
    ...(userDetails?.role === "admin"
      ? [
          {
            key: "admindashboard",
            label: "Admin Dashboard",
            icon: <DashboardOutlined />,
            onClick: () => navigate("/admin/enquiries"),
          },
        ]
      : []),
    {
      key: "logout",
      label: <span style={{ color: "red" }}>Logout</span>,
      icon: <LogoutOutlined style={{ color: "red" }} />,
      onClick: logout,
    },
  ];

  const navItems = [
    { label: "HOME", path: "/" },
    { label: "TRIPS", path: "/trips" },
    { label: "SCUBA", path: "/scuba" },
    { label: "SKYDIVE", path: "/skydive" },
    { label: "ABOUT US", path: "/about-us" },
  ];

  // const policiesPaths = ["/terms", "/refund-policy", "/privacy-policy"];
  // const isPolicyActive = policiesPaths.includes(location.pathname);

  // const policiesMenuItems = [
  //   {
  //     key: "terms",
  //     label: "Terms & Conditions",
  //     onClick: () => navigate("/terms"),
  //   },
  //   {
  //     key: "refund-policy",
  //     label: "Refund Policy",
  //     onClick: () => navigate("/refund-policy"),
  //   },
  //   {
  //     key: "privacy-policy",
  //     label: "Privacy Policy",
  //     onClick: () => navigate("/privacy-policy"),
  //   },
  // ];

  const isMobile = !screens.md;

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };
  const phoneDigits = waNumber.replace(/\D/g, "");

  const isPhoneValid = phoneDigits.length >= 8 && phoneDigits.length <= 15;

  const canSendOtp =
    isPhoneValid &&
    Boolean(captcha?.captchaId && captcha?.svg) &&
    captchaCode.trim().length === 5 &&
    !loading;

  const canVerifyOtp =
    otp.trim().length === 6 &&
    secondsRemaining > 0 &&
    verifyAttempts < 5 &&
    !loading;

  const canResendOtp = secondsRemaining === 0 || verifyAttempts >= 5;

  const countdownText = `${Math.floor(secondsRemaining / 60)
    .toString()
    .padStart(2, "0")}:${(secondsRemaining % 60).toString().padStart(2, "0")}`;
  return (
    <>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(148,163,184,0.18)",
          boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
        }}
      >
        <Row
          align="middle"
          justify="space-between"
          style={{
            padding: "8px 16px",
            maxWidth: "100vw",
            margin: "0 auto",
          }}
        >
          <Col>
            <Link
              to="/"
              style={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                gap: 10,
                color: "inherit",
              }}
            >
              <Avatar
                shape="square"
                src={navbarDetails?.logoUrl}
                alt="logo"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  boxShadow: "0 4px 12px rgba(15,23,42,0.18)",
                }}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  lineHeight: 1.1,
                }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 18,
                    letterSpacing: 1.8,
                  }}
                >
                  DIVE HRZN
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  Explore life between the horizon
                </span>
              </div>
            </Link>
          </Col>

          <Col>
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={openDrawer}
                style={{
                  borderRadius: 999,
                  border: "1px solid #e2e8f0",
                  width: 40,
                  height: 40,
                  boxShadow: "0 4px 10px rgba(15,23,42,0.08)",
                }}
              />
            ) : (
              <Row align="middle" gutter={12} wrap={false}>
                <Col>
                  <div
                    style={{
                      display: "flex",
                      gap: 4,
                      padding: "4px 6px",
                      borderRadius: 999,
                      background:
                        "linear-gradient(145deg, #f9fafb, #e5e7eb, #f9fafb)",
                      boxShadow:
                        "inset 0 1px 0 rgba(255,255,255,0.8), 0 8px 20px rgba(15,23,42,0.05)",
                    }}
                  >
                    {navItems.map((it) => {
                      const active = isActive(it.path);
                      return (
                        <Button
                          key={it.label}
                          type="text"
                          disabled={it.disabled}
                          onClick={() => navigate(it.path)}
                          style={{
                            borderRadius: 999,
                            padding: "4px 14px",
                            fontSize: 13,
                            fontWeight: active ? 600 : 400,
                            background: active ? "#ffffff" : "transparent",
                            boxShadow: active
                              ? "0 2px 8px rgba(15,23,42,0.14)"
                              : "none",
                            border: active
                              ? "1px solid rgba(59,130,246,0.18)"
                              : "1px solid transparent",
                            transition: "all 0.18s ease",
                            transform: active ? "translateY(-1px)" : "none",
                          }}
                        >
                          {it.label}
                        </Button>
                      );
                    })}
                    {/* <Dropdown
                      menu={{ items: policiesMenuItems }}
                      trigger={["hover", "click"]}
                    >
                      <Button
                        type="text"
                        style={{
                          borderRadius: 999,
                          padding: "4px 14px",
                          fontSize: 13,
                          fontWeight: isPolicyActive ? 600 : 400,
                          background: isPolicyActive
                            ? "#ffffff"
                            : "transparent",
                          boxShadow: isPolicyActive
                            ? "0 2px 8px rgba(15,23,42,0.14)"
                            : "none",
                          border: isPolicyActive
                            ? "1px solid rgba(59,130,246,0.18)"
                            : "1px solid transparent",
                          transition: "all 0.18s ease",
                          transform: isPolicyActive
                            ? "translateY(-1px)"
                            : "none",
                        }}
                      >
                        POLICIES
                      </Button>
                    </Dropdown> */}
                  </div>
                </Col>

                <Col>
                  {!accessToken ? (
                    <Button
                      onClick={openAuthModal}
                      style={{
                        borderRadius: 999,
                        padding: "0 20px",
                        height: 36,
                        fontWeight: 500,
                        border: "none",
                        background: "linear-gradient(135deg, #0f172a, #020617)",
                        color: "#ffffff",
                        boxShadow: "0 10px 24px rgba(15,23,42,0.35)",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        transition:
                          "transform 0.15s ease, box-shadow 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow =
                          "0 14px 30px rgba(15,23,42,0.42)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "none";
                        e.currentTarget.style.boxShadow =
                          "0 10px 24px rgba(15,23,42,0.35)";
                      }}
                    >
                      <span>Sign In</span>
                    </Button>
                  ) : (
                    <Dropdown
                      menu={{ items: profileMenuItems }}
                      placement="bottomRight"
                    >
                      <Button
                        style={{
                          borderRadius: 999,
                          padding: "0 12px",
                          height: 36,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          border: "1px solid #e2e8f0",
                          background: "#ffffff",
                          boxShadow: "0 6px 18px rgba(15,23,42,0.12)",
                          transition:
                            "box-shadow 0.15s ease, transform 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow =
                            "0 10px 24px rgba(15,23,42,0.20)";
                          e.currentTarget.style.transform = "translateY(-1px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow =
                            "0 6px 18px rgba(15,23,42,0.12)";
                          e.currentTarget.style.transform = "none";
                        }}
                      >
                        <Avatar
                          size={24}
                          src={userDetails?.profileImage}
                          style={{
                            backgroundColor: "#9bd1fbff",
                            boxShadow: "0 2px 6px rgba(15,23,42,0.25)",
                          }}
                        >
                          <UserOutlined />
                        </Avatar>

                        <span
                          style={{
                            maxWidth: 140,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                          }}
                        >
                          {userDetails?.displayName ||
                            userDetails?.email?.split("@")[0]}
                        </span>
                      </Button>
                    </Dropdown>
                  )}
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </div>

      <Drawer
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Avatar
              src={navbarDetails?.logoUrl}
              alt="logo"
              style={{ width: 28, height: 28, borderRadius: 8 }}
            />
            <span style={{ fontWeight: 600 }}>DIVE HRZN</span>
          </div>
        }
        placement="left"
        onClose={closeDrawer}
        open={drawerOpen}
        styles={{ body: { padding: 0 } }}
      >
        <Divider style={{ margin: 0 }} />

        <div style={{ padding: "12px 16px" }}>
          <Menu
            selectedKeys={[location.pathname]}
            mode="inline"
            style={{ borderRight: 0, fontSize: 15 }}
            onClick={({ key }) => {
              if (key === "profile") return navigate("/profile");
              if (key === "logout") return logout();
              goto(key);
            }}
            items={[
              {
                key: "/",
                label: "HOME",
              },
              { label: "TRIPS", key: "/trips" },
              {
                key: "/scuba",
                label: "SCUBA",
              },
              {
                key: "/skydive",
                label: "SKYDIVE",
              },
              {
                key: "/about-us",
                label: "ABOUT US",
              },
              // {
              //   key: "policies",
              //   label: "POLICIES",
              //   children: [
              //     {
              //       key: "/terms",
              //       label: "Terms & Conditions",
              //     },
              //     {
              //       key: "/refund-policy",
              //       label: "Refund Policy",
              //     },
              //     {
              //       key: "/privacy-policy",
              //       label: "Privacy Policy",
              //     },
              //   ],
              // },
            ]}
          />

          <Divider />

          {!accessToken ? (
            <Button
              block
              color="black"
              icon={<GoogleOutlined />}
              size="large"
              onClick={openAuthModal}
              style={{ borderRadius: 999 }}
            >
              Sign In
            </Button>
          ) : (
            <Menu
              mode="inline"
              style={{ borderRight: 0, marginTop: 10 }}
              onClick={({ key }) => {
                if (key === "profile") {
                  navigate("/profile");
                  closeDrawer();
                }
                if (key === "myEnquiries") {
                  navigate("/my-enquiries");
                  closeDrawer();
                }
                if (key === "admindashboard") {
                  navigate("/admin/enquiries");
                  closeDrawer();
                }
                if (key === "logout") {
                  logout();
                  closeDrawer();
                }
              }}
              items={[
                {
                  key: "profile",
                  icon: <UserOutlined />,
                  label: "My Profile",
                },
                {
                  key: "myEnquiries",
                  icon: <FileSearchOutlined />,
                  label: "My Enquiries",
                },
                ...(userDetails?.role === "admin"
                  ? [
                      {
                        key: "admindashboard",
                        icon: <DashboardOutlined />,
                        label: "Admin Dashboard",
                      },
                    ]
                  : []),
                {
                  key: "logout",
                  icon: <LogoutOutlined style={{ color: "red" }} />,
                  label: <span style={{ color: "red" }}>Logout</span>,
                },
              ]}
            />
          )}
        </div>
      </Drawer>

      <Modal
        title="Verify your WhatsApp number"
        open={phoneModalOpen}
        onCancel={() => {
          setPhoneModalOpen(false);
          setPendingUser(null);
          setLoading(false);
          setCaptcha(null);
          setCaptchaCode("");
          setOtp("");
          setOtpSent(false);
          Cookies.remove("token");
        }}
        maskClosable={false}
        footer={null}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          Verify your WhatsApp number to complete your DIVE HRZN sign-in.
        </Typography.Paragraph>

        <div style={{ marginBottom: 16 }}>
          <PhoneInput
            international
            defaultCountry="IN"
            value={fullWhatsAppNumber}
            onChange={(value) => {
              if (!value) {
                setCountryCode("+91");
                setWaNumber("");
                return;
              }

              setCountryCode("");
              setWaNumber(value);
            }}
            disabled={otpSent}
            placeholder="Enter WhatsApp number"
            className="whatsapp-phone-input"
          />
        </div>

        {!otpSent ? (
          <>
            <div style={{ marginBottom: 12 }}>
              <Typography.Text strong>Security CAPTCHA</Typography.Text>
              <Space align="center" style={{ display: "flex", marginTop: 6 }}>
                <div
                  aria-label="CAPTCHA challenge"
                  style={{
                    minWidth: 180,
                    minHeight: 58,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#000000",
                    border: "1px solid #333333",
                    borderRadius: 6,
                    overflow: "hidden",
                  }}
                >
                  {captcha?.svg ? (
                    <img
                      src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(captcha.svg)}`}
                      alt="Security CAPTCHA"
                      style={{
                        width: 170,
                        height: 55,
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: 6,
                        display: "block",
                      }}
                    />
                  ) : (
                    <Typography.Text type="secondary">
                      Loading CAPTCHA…
                    </Typography.Text>
                  )}
                </div>
                <Button
                  aria-label="Refresh CAPTCHA"
                  onClick={refreshWhatsAppCaptcha}
                >
                  Refresh
                </Button>
              </Space>
              <Input
                placeholder="ENTER THE CHARACTERS SHOWN"
                value={captchaCode}
                onChange={(e) =>
                  setCaptchaCode(
                    e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z2-9]/g, "")
                      .slice(0, 5),
                  )
                }
                maxLength={5}
                autoComplete="off"
                style={{
                  marginTop: 8,
                  textTransform: "uppercase",
                }}
              />
            </div>
            <Button
              type="primary"
              block
              loading={loading}
              disabled={!canSendOtp}
              onClick={() => handleSendWhatsAppOtp(false)}
            >
              Send WhatsApp OTP
            </Button>
          </>
        ) : (
          <>
            <Typography.Paragraph>
              Enter the 6-digit code sent to{" "}
              <strong>{fullWhatsAppNumber}</strong>.
            </Typography.Paragraph>
            <Input
              placeholder="6-digit OTP"
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                setOtpError("");
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              size="large"
              style={{ marginBottom: 12, letterSpacing: 5 }}
              onPressEnter={handleVerifyWhatsAppOtp}
            />
            <Typography.Paragraph
              style={{ textAlign: "center", marginBottom: 16 }}
            >
              {secondsRemaining > 0 ? (
                <>
                  OTP expires in{" "}
                  <Typography.Text strong>{countdownText}</Typography.Text>
                </>
              ) : (
                <Typography.Text type="danger">
                  OTP expired. Request a new OTP.
                </Typography.Text>
              )}
            </Typography.Paragraph>

            <Typography.Paragraph type="secondary">
              Failed attempts: {verifyAttempts}/5
            </Typography.Paragraph>
            {otpError && (
              <Typography.Text
                type="danger"
                style={{ display: "block", marginBottom: 12 }}
                role="alert"
              >
                {otpError}
              </Typography.Text>
            )}
            <Button
              type="primary"
              block
              loading={loading}
              disabled={!canVerifyOtp}
              onClick={handleVerifyWhatsAppOtp}
            >
              Verify OTP & Continue
            </Button>
            <Button
              type="link"
              block
              disabled={loading}
              onClick={() => {
                setOtpSent(false);
                setOtp("");
                refreshWhatsAppCaptcha();
              }}
            >
              Change number
            </Button>
            <Button
              type="link"
              block
              disabled={loading || !canResendOtp}
              onClick={async () => {
                setOtpSent(false);
                setOtp("");
                setOtpError("");
                setSecondsRemaining(0);
                setVerifyAttempts(0);
                setCaptcha(null);
                setCaptchaCode("");
                await refreshWhatsAppCaptcha();
              }}
            >
              Resend OTP (complete a new CAPTCHA)
            </Button>
          </>
        )}
      </Modal>

      <AuthTabsModal
        visible={authModalOpen}
        onClose={closeAuthModal}
        loading={loading}
        handleGoogleSignIn={handleGoogleSignIn}
        logoUrl={navbarDetails?.logoUrl}
      />
    </>
  );
};

export default Navbar;
